/**
 * MUI Main Component
 * 
 * This component is the Material UI equivalent of the original Main component.
 * It renders the main application container for the Room Designer application.
 */

import React, { useEffect, useState, useRef } from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useCookies } from 'react-cookie';
import MuiSplashScreen from '../organisms/SplashScreen';
import WalkthroughGuide from '../organisms/WalkthroughGuide';
import DesignNameDialog from '../organisms/DesignNameDialog';
import Page from './Page';
import ModalManager from '../services/ModalManager';
import ToolContext from '../services/ToolContext';
import ReducersIndex from 'reduxModules/ducks';
import { camelCase } from 'lodash';
import Load from '../services/Load';
import isEmpty from 'lodash/isEmpty';

// Import ToolContextType from its source file
import { ToolContextType } from '../services/ToolContext';
import type { RootState } from 'types/redux/state';

// Define our prop interface using the existing ToolContextType
interface MainProps {
  tool: ToolContextType;
}

/**
 * MUI Main component
 * 
 * @param props - Component props
 * @returns The MUI Main component
 */
const MuiMain: React.FC<MainProps> = ({ tool }) => {
  const redirect = tool.constants.tool.goto;
  if (redirect) window.location.href = redirect;

  // Check if splash screen should be skipped
  // const skipSplash = process.env.REACT_APP_SKIP_SPLASH === 'true';
  const skipSplash = true;
  
  const [splashOpen, setSplashOpen] = useState<boolean>(true);
  const [projectLoaded, setProjectLoaded] = useState<boolean>(false);
  const [firstTimeUser, setFirstTimeUser] = useState<boolean>(true);
  const [designNameDialogOpen, setDesignNameDialogOpen] = useState<boolean>(false);
  // const [showWalkthrough, setShowWalkthrough] = useState<boolean>(false); // No longer needed - using tutorial modal
  
  const isFirstTimeUser = useSelector((state: RootState) => state.ui.isFirstTimeUser);
  const { search } = useLocation();
  // target removed - CRD no longer uses launch modal target (replaced by walkthrough guide)
  const target = undefined;
  const cookieName = tool.constants.tool.cookie;

  const [cookies, setCookie] = useCookies([cookieName]);

  const dispatch = useDispatch();

  // Get project data from Redux
  const projectName = useSelector((state: RootState) => state.container?.project?.name || '');
  const projectLocationId = useSelector((state: RootState) => state.container?.project?.locationId || '');

  const toolName = camelCase(tool.constants.tool.fullName);
  const [name, setName] = useState<string>(toolName);
  
  /**
   * Check if guide should auto-show based on version changes or inactivity
   */
  const checkShouldShowGuide = (): boolean => {
    // Check version change (would need to store last version in localStorage)
    const storedVersion = localStorage.getItem('room_designer_version');
    const currentVersion = process.env.REACT_APP_VERSION || '1.0.0';
    
    if (storedVersion && storedVersion !== currentVersion) {
      const [storedMajor, storedMinor] = storedVersion.split('.');
      const [currentMajor, currentMinor] = currentVersion.split('.');
      
      // Show guide if major or minor version changed
      if (storedMajor !== currentMajor || storedMinor !== currentMinor) {
        localStorage.setItem('room_designer_version', currentVersion);
        return true;
      }
    }
    
    // Check last usage time (3+ months)
    const lastUsed = localStorage.getItem('room_designer_last_used');
    if (lastUsed) {
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      
      if (new Date(lastUsed) < threeMonthsAgo) {
        return true;
      }
    }
    
    // Update last used time
    localStorage.setItem('room_designer_last_used', new Date().toISOString());
    if (!storedVersion) {
      localStorage.setItem('room_designer_version', currentVersion);
    }
    
    // Only show for first-time users
    return false;
  };

  // Check for guide continuation when component mounts
  useEffect(() => {
    const continueGuide = sessionStorage.getItem('continueGuide');
    const tutorialStep = sessionStorage.getItem('tutorialStep');
    
    // TEMPORARY: Disable tutorial modal to test WalkthroughGuide
    const USE_WALKTHROUGH_GUIDE = true;
    
    
    if (continueGuide === 'true' && tool?.tutorialSteps && !guideContinuationHandled.current) {
      guideContinuationHandled.current = true; // Mark as handled to prevent re-runs
      
      // Auto-skip splash and continue tutorial
      setSplashOpen(false);
      setProjectLoaded(true);
      
      // Clear only the continueGuide flag, leave tutorialStep for WalkthroughGuide to read
      sessionStorage.removeItem('continueGuide');
      // sessionStorage.removeItem('tutorialStep'); // Let WalkthroughGuide handle this
    }
  }, [tool?.tutorialSteps, dispatch, projectLoaded, splashOpen]); // Add all dependencies

  // Separate effect to handle tutorial config after project is loaded
  useEffect(() => {
    if (guideContinuationHandled.current && projectLoaded && !splashOpen && tool?.tutorialSteps) {

      const timer = setTimeout(() => {
        dispatch(ReducersIndex.uiReducers.setTutorialConfig(tool.tutorialSteps));

        // Don't override the step - WalkthroughGuide will handle it
        // The step should already be set from sessionStorage or initial mount

        // Don't open modal - WalkthroughGuide will handle display

        // Reset the handled flag so it doesn't interfere with future guides
        guideContinuationHandled.current = false;
      }, 500); // Shorter timeout since we know project is loaded

      return () => clearTimeout(timer);
    }
  }, [projectLoaded, splashOpen, tool?.tutorialSteps, dispatch]);

  // Handle showing design name dialog after project loads for new designs
  useEffect(() => {
    if (projectLoaded && !splashOpen) {
      // Check if this is a new design (no savekey in URL means new design)
      const urlParams = new URLSearchParams(search);
      const isExistingDesign = urlParams.has('savekey');

      // Check if tutorial/walkthrough is completed
      const hasCompletedTutorial = localStorage.getItem('hasCompletedTutorial') === 'true';

      // Show dialog for new designs if tutorial is completed
      if (!isExistingDesign && hasCompletedTutorial) {
        // Delay slightly to ensure the designer is fully rendered
        const timer = setTimeout(() => {
          setDesignNameDialogOpen(true);
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [projectLoaded, splashOpen, search]);

  useEffect(() => {
    dispatch(ReducersIndex.uiReducers.setTool(toolName));
    setName(toolName);

    // Check for new design name from sessionStorage (set by DesignNameDialog)
    const newDesignName = sessionStorage.getItem('newDesignName');
    if (newDesignName) {
      // Set the project name in Redux
      dispatch(ReducersIndex.container.setProject({ name: newDesignName }));
      // Clear it from sessionStorage after using
      sessionStorage.removeItem('newDesignName');
    }

    // Check if guide should be shown based on version or time
    const shouldShowGuide = checkShouldShowGuide();

    // Auto-skip splash if needed.
    // Skip for existing designs (savekey present) — Load.js manages splash and
    // projectLoaded in that case, preventing a stale view from flashing during load.
    const urlParams = new URLSearchParams(search);
    const isExistingDesign = urlParams.has('savekey');
    if (!isExistingDesign && (skipSplash || shouldShowGuide)) {
      const timer = setTimeout(() => {
        closeSplash();
      }, 100);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolName, skipSplash, tool, dispatch]);

  // Deal with the cookies thing
  useEffect(() => {
    if (!isEmpty(cookies) && cookieName in cookies) {
      dispatch(ReducersIndex.uiReducers.setIsFirstTimeUser(false));
      setFirstTimeUser(false);
    }
  }, [cookies, cookieName, dispatch]);

  useEffect(() => {
    if (!isFirstTimeUser) {
      const date = new Date();
      const today = date.getDate();
      date.setMonth(date.getMonth() + 6);
      if (date.getDate() !== today) {
        date.setDate(0);
      }
      setCookie(cookieName, true, { expires: date });
    }
  }, [isFirstTimeUser, cookieName, setCookie]);

  /**
   * Close the splash screen
   */
  const closeSplash = (): void => {
    setSplashOpen(false);
    setProjectLoaded(true);
    
    // Show walkthrough only for first-time users (disabled for now)
    // if (firstTimeUser || checkShouldShowGuide()) {
    //   setTimeout(() => {
    //     setShowWalkthrough(true);
    //   }, 500);
    // }
  };
  
  // Walkthrough handlers removed - now handled by tutorial modal

  const headerRef = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState<number>(0);
  const guideContinuationHandled = useRef<boolean>(false);
  
  useEffect(() => {
    if (headerRef.current) {
      const bbox = headerRef.current.getBoundingClientRect();
      setTop(bbox.bottom);
    }
  }, [splashOpen, projectLoaded]);


  return (
    <Box className="flex-grow-1 container-fluid p-2">
      <ToolContext.Provider value={tool}>
        {name && (
          // @ts-expect-error - Load component is a JavaScript file that needs proper typing
          <Load 
            params={search} 
            setProjectLoaded={setProjectLoaded} 
            setSplashOpen={setSplashOpen} 
            target={target} 
            setFirstTimeUser={setFirstTimeUser} 
          />
        )}
        {splashOpen && !projectLoaded && !skipSplash && <MuiSplashScreen closeSplash={closeSplash} />}
        {(!splashOpen && projectLoaded) && (
          <>
            <Page
              ref={headerRef}
              showChild={!firstTimeUser}
              onOpenDesignNameDialog={() => setDesignNameDialogOpen(true)}
            />
          </>
        )}
        {/* WalkthroughGuide for tool pages - not designs page */}
        {(() => {
          const shouldRender = location.pathname !== '/designs' && projectLoaded;
          return shouldRender && <WalkthroughGuide />;
        })()}
        <ModalManager top={top} />

        {/* Design Name Dialog for new designs */}
        <DesignNameDialog
          open={designNameDialogOpen}
          onClose={() => setDesignNameDialogOpen(false)}
          onConfirm={(data) => {
            setDesignNameDialogOpen(false);
            if (data.name) {
              // Set the project name and location in Redux
              const projectData = {
                name: data.name,
                locationId: data.workplaceLocation || ''
              };
              dispatch(ReducersIndex.container.setProject(projectData));
            }
          }}
          mode="new"
          initialName={projectName || ''}
          initialLocationId={projectLocationId}
          designType="Conference Room"
          organizationId={sessionStorage.getItem('selectedOrgId') || ''}
        />
      </ToolContext.Provider>
    </Box>
  );
};

export default MuiMain;
